import type { ISubscription } from "@features/shared/entities/subscription";

export type RestoreStatus = "idle" | "processing" | "success" | "pending" | "failed" | "mandate_revoked";

// POST /subscription/restore answers with the billing method of the restored
// subscription, read from Solidgate at restore time ("card", "upi",
// "pix-automatico", "paypal-vault", ...). UPI and Pix Automático subscriptions
// renew through a mandate: the charge is asynchronous (UPI: a pre-debit notice,
// the approve lands about a day later; Pix: scheduled at least two days ahead
// and retried for up to a week) and the customer can revoke the mandate at any
// time.
export type RestoreSubscriptionResponse = { paymentType?: string | null };

const normalizePaymentType = (paymentType?: string | null) => (paymentType ?? "").trim().toLowerCase();

export const isUpiPaymentType = (paymentType?: string | null): boolean => normalizePaymentType(paymentType) === "upi";

export const isPixAutomaticoPaymentType = (paymentType?: string | null): boolean =>
    normalizePaymentType(paymentType) === "pix-automatico";

export const isMandatePaymentType = (paymentType?: string | null): boolean =>
    isUpiPaymentType(paymentType) || isPixAutomaticoPaymentType(paymentType);

// 409 body of the restore when the UPI / Pix mandate behind the subscription
// was cancelled by the customer: Solidgate would accept the restore, charge, get
// declined and cancel again, so the backend refuses and the student is sent to
// set up a new subscription instead.
export const RESTORE_MANDATE_REVOKED_ERROR = "mandate_revoked";

export const isMandateRevokedError = (error: unknown): boolean =>
    !!error &&
    typeof error === "object" &&
    "error" in error &&
    (error as { error?: unknown }).error === RESTORE_MANDATE_REVOKED_ERROR;

// What counts as a confirmed restore while polling the subscriptions list.
// Card: Solidgate charges at once and the renew webhook pushes endDate forward
// within seconds, so the extended paid period is the proof. UPI / Pix: the
// charge is asynchronous and endDate stays in the past until it lands; the
// restore webhook turning auto-renew back on is all the confirmation there is,
// and it is enough — access is open from then.
export const isRestoreConfirmed = (
    subscription: ISubscription,
    paymentType?: string | null,
    now: number = Date.now()
): boolean => {
    if (!subscription.isAutoRenewable) return false;
    if (isMandatePaymentType(paymentType)) return true;

    return new Date(subscription.endDate).getTime() > now + 60 * 1000;
};

// Polling ran out while still "processing". For a card that means the charge
// did not go through. For UPI / Pix the restore webhook itself was late, which
// is not a failure: the restore was accepted and completes on its own.
export const restoreTimeoutStatus = (paymentType?: string | null): RestoreStatus =>
    isMandatePaymentType(paymentType) ? "pending" : "failed";

// Whether the restored subscription is waiting for a mandate debit (UPI, Pix)
// to be confirmed. Solidgate charges on restore only when the paid period is
// already over; a subscription restored while still paid up keeps its dates and
// renews on the regular date, so the "will confirm the payment" copy would be
// misleading.
export const isRestoreAwaitingMandateCharge = (
    subscription: Pick<ISubscription, "endDate">,
    paymentType?: string | null,
    now: number = Date.now()
): boolean => isMandatePaymentType(paymentType) && new Date(subscription.endDate).getTime() <= now;
